import { EndpointBuilder } from '@reduxjs/toolkit/query';
import {
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta
} from '@reduxjs/toolkit/query';
import { BaseQueryFn } from '@reduxjs/toolkit/query';
import {
  ModulesResponse,
  ModulesResponseModified,
  PermissionsResponse,
  ResponseBaseWithoutData,
  RolesResponse,
  RolesResponseModified,
  UpdateRoleResponse,
  UpdateUserResponse,
  UserInfoResponse,
  UserInfoResponseModified,
  UsersResponse,
  UsersResponseModified
} from '../types';
import {
  ChangePasswordPayload,
  CreateUserPayload,
  UpdateUserPayload,
  UserAttributes,
  UsersRecord,
  UsersRecordModified
} from '../types/records/user';
import { CreateRolePayload, UpdateRolePayload } from '../types/records/roles';
import { CreatePermissionPayload } from '../types/records/permissions';

export const userAccessPanelAPIEndpoints = (
  builder: EndpointBuilder<
    BaseQueryFn<
      string | FetchArgs,
      unknown,
      FetchBaseQueryError,
      NonNullable<unknown>,
      FetchBaseQueryMeta
    >,
    string,
    'userAccessPanelAPI'
  >
) => ({
  getUsers: builder.query<
    UsersResponseModified,
    { limit: string; paginatedToken?: string | null; query?: string | null }
  >({
    query: ({ limit, paginatedToken, query }) => ({
      url: '/auth/users',
      params: {
        limit,
        paginatedToken,
        query
      }
    }),
    keepUnusedDataFor: 0,
    transformResponse: (response: UsersResponse): UsersResponseModified => {
      const newUsers: UsersRecordModified[] = response.data.map(
        (user: UsersRecord) => {
          // Flattening attributes
          const flattenAttrs = (user.Attributes as UserAttributes[]).reduce(
            (acc: Partial<UsersRecordModified>, obj: UserAttributes) => {
              (acc as { [key: string]: string })[obj.Name] = obj.Value;
              return acc;
            },
            {} as Partial<UsersRecordModified>
          );

          // Merge flattened attributes with the rest of the user object, ensuring default values
          const newUser: UsersRecordModified = {
            email: flattenAttrs.email || '', // Default to empty string if undefined
            email_verified: flattenAttrs.email_verified || false,
            phone_number: flattenAttrs.phone_number || '', // Default to empty string
            phone_number_verified: flattenAttrs.phone_number_verified || false, // Convert to boolean
            family_name: flattenAttrs.family_name || '', // Default to empty string
            given_name: flattenAttrs.given_name || '', // Default to empty string
            sub: flattenAttrs.sub || '', // Default to empty string
            Enabled: user.Enabled,
            UserCreateDate: user.UserCreateDate,
            UserLastModifiedDate: user.UserLastModifiedDate,
            UserStatus: user.UserStatus,
            Username: user.Username
          };

          return newUser;
        }
      );

      return {
        status: response.status,
        data: newUsers,
        message: response.message || '',
        paginatedToken: response.paginatedToken
      };
    },

    providesTags: ['getUsers']
  }),
  getUserInfo: builder.query<UserInfoResponseModified, { param: string }>({
    query: ({ param }) => ({
      url: `/auth/users/${param}`
    }),
    providesTags: ['getUserInfo'],
    transformResponse: (
      response: UserInfoResponse
    ): UserInfoResponseModified => {
      const { UserAttributes, ...newData } = response.data;

      // Flatten the UserAttributes array into a single object
      const newAttributes = UserAttributes.reduce((acc, e) => {
        (acc as { [key: string]: string })[e.Name] = e.Value;
        return acc;
      }, {} as Partial<UsersRecordModified>);

      // Merge the flattened attributes with the rest of the data
      const newUser: UsersRecordModified = {
        email: newAttributes.email || '', // Default to empty string if undefined
        email_verified: newAttributes.email_verified || false,
        phone_number: newAttributes.phone_number || '', // Default to empty string
        phone_number_verified: newAttributes.phone_number_verified || false, // Convert to boolean
        family_name: newAttributes.family_name || '', // Default to empty string
        given_name: newAttributes.given_name || '', // Default to empty string
        sub: newAttributes.sub || '', // Default to empty string
        Enabled: newData.Enabled,
        UserCreateDate: newData.UserCreateDate,
        UserLastModifiedDate: newData.UserLastModifiedDate,
        UserStatus: newData.UserStatus,
        Username: newData.Username,
        Role: newData.Role
      };
      return {
        status: response.status,
        data: newUser,
        message: response.message || ''
      };
    }
  }),
  getRoles: builder.query<RolesResponseModified, void>({
    query: () => ({
      url: '/auth/roles'
    }),
    transformResponse: (response: RolesResponse): RolesResponseModified => {
      const newRolesData = response.data.map((role) => {
        const { permissions, ...rest } = role;
        const permissionsString = permissions.map((e) => e.name).join(',');
        return {
          ...rest,
          allowedPermissions: permissionsString,
          permissions: permissions
        };
      });
      return {
        status: response.status,
        data: newRolesData,
        message: response.message || ''
      };
    },
    providesTags: ['roles']
  }),
  updateRole: builder.mutation<
    UpdateRoleResponse,
    { payload: UpdateRolePayload }
  >({
    query: ({ payload }) => ({
      url: '/auth/roles/update-user-role',
      method: 'POST',
      body: payload
    }),
    invalidatesTags: ['getUserInfo']
  }),
  updateUser: builder.mutation<
    UpdateUserResponse,
    { payload: UpdateUserPayload }
  >({
    query: ({ payload }) => ({
      url: '/auth/users',
      method: 'PUT',
      body: payload
    }),
    invalidatesTags: ['getUsers']
  }),
  resendUserInvitation: builder.query<
    ResponseBaseWithoutData,
    { email: string | undefined }
  >({
    query: ({ email }) => ({
      url: `/auth/users/resend-user-invitation/?email=${email}`
    })
  }),
  createNewUser: builder.mutation<
    ResponseBaseWithoutData,
    { payload: CreateUserPayload }
  >({
    query: ({ payload }) => ({
      url: 'auth/users',
      method: 'POST',
      body: payload
    }),
    invalidatesTags: ['getUsers']
  }),
  getPermissions: builder.query<PermissionsResponse, string>({
    query: (params) => ({
      url: `/auth/permissions${params}`
    }),
    providesTags: ['getPermissions']
  }),
  createPermission: builder.mutation<
    ResponseBaseWithoutData,
    { payload: CreatePermissionPayload }
  >({
    query: ({ payload }) => ({
      url: 'auth/permissions',
      method: 'POST',
      body: payload
    }),
    invalidatesTags: ['getPermissions']
  }),
  updatePermission: builder.mutation<
    ResponseBaseWithoutData,
    { payload: CreatePermissionPayload }
  >({
    query: ({ payload }) => ({
      url: 'auth/permissions',
      method: 'PUT',
      body: payload
    }),
    invalidatesTags: ['getPermissions']
  }),
  deletePermission: builder.mutation<ResponseBaseWithoutData, { name: string }>(
    {
      query: ({ name }) => ({
        url: 'auth/permissions',
        params: { name },
        method: 'DELETE'
      }),
      invalidatesTags: ['getPermissions']
    }
  ),
  createRole: builder.mutation<
    ResponseBaseWithoutData,
    { payload: CreateRolePayload }
  >({
    query: ({ payload }) => ({
      url: 'auth/roles',
      method: 'POST',
      body: payload
    }),
    invalidatesTags: ['roles']
  }),
  updateRoleData: builder.mutation<
    ResponseBaseWithoutData,
    { payload: CreateRolePayload }
  >({
    query: ({ payload }) => ({
      url: 'auth/roles',
      method: 'PUT',
      body: payload
    }),
    invalidatesTags: ['roles']
  }),
  deleteRole: builder.mutation<ResponseBaseWithoutData, { name: string }>({
    query: (name) => ({
      url: 'auth/roles',
      params: name,
      method: 'DELETE'
    }),
    invalidatesTags: ['roles']
  }),
  changePassword: builder.mutation<
    ResponseBaseWithoutData,
    { payload: ChangePasswordPayload }
  >({
    query: ({ payload }) => ({
      url: '/auth/users/change-password',
      method: 'PATCH',
      body: payload
    })
  }),
  getModules: builder.query<ModulesResponseModified, void>({
    query: () => ({
      url: `/auth/modules`
    }),
    transformResponse: (response: ModulesResponse): ModulesResponseModified => {
      const newResponse = response.data.map((module) => {
        return {
          label: module.name,
          value: module.pathname,
          id: module.id,
          name: module.name
        };
      });
      return {
        status: response.status,
        data: newResponse,
        message: response.message || ''
      };
    }
  })
});
